import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Settings } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import { useFeatureFlag, useIsMobile } from '@uplift/hooks';
import { useICSSubscriptionQuery } from '@uplift/queries';
import { AvailabilitySettingsModalType } from '@uplift/types';

import { ClosableMenu, ClosableMenuItem } from '../../lib/simple-menu';
import { EditIdealCaseloadModal } from './EditIdealCaseloadModal.component';
import { EditTimezoneModal } from './EditTimezoneModal.component';
import { SubscribeGoogleCalendarModal } from './SubscribeGoogleCalendarModal.component';
import { UnsubscribeGoogleCalendarModal } from './UnsubscribeGoogleCalendarModal.component';
import { PublicICSSubscriptionModal } from '../../molecules';

export type AvailabilitySettingsProps = {
  color?: 'dark' | 'light';
};


const useStyles = makeStyles()(theme => ({
  light: {
    color: theme.palette.text.secondary
  },
  dark: {
    color: theme.palette.common.white
  },
}));

export const AvailabilitySettings: React.FC<AvailabilitySettingsProps> = ({ color = 'light' }) => {
  const me = useCurrentuser();
  const { classes, cx } = useStyles();
  const USER_PUBLIC_ICS_SUBSCRIPTION = useFeatureFlag('USER_PUBLIC_ICS_SUBSCRIPTION');
  const [ subscribeToPublicICSModalOpen, setSubscribeToPublicICSMModalOpen ] = React.useState(false);
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLElement>();
  const location = useLocation();
  const isMobile = useIsMobile('sm');
  const queryParams = useMemo(() => new URLSearchParams(location.search), [ location.search ]);
  const [ openedModal, setOpenedModal ] = React.useState<AvailabilitySettingsModalType | null>(() => {
    return queryParams.get('modal') as AvailabilitySettingsModalType || null;
  });

  const { data: icsSubscription } = useICSSubscriptionQuery(me.id, { enabled: true });
  const { push } = useHistory();

  const buttonClassName = cx({
    [classes.light]: color === 'light',
    [classes.dark]: color === 'dark',
  });

  const handleOnClose = useCallback(() => {
    setOpenedModal(null);
    push('/schedule');
  }, [ push ]);

  return (
    <>
      <IconButton title="Settings" size="small" component="span" onClick={e => setAnchorEl(e.target as HTMLElement)} className={buttonClassName}>
        <Settings color="inherit" />
      </IconButton>
      <ClosableMenu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ClosableMenuItem onClick={() => setOpenedModal(AvailabilitySettingsModalType.timezone)}>Time Zone</ClosableMenuItem>
        <ClosableMenuItem onClick={() => setOpenedModal(AvailabilitySettingsModalType.idealCaseload)}>Ideal Caseload</ClosableMenuItem>
        {!isMobile && (
          <>
            {icsSubscription && <ClosableMenuItem onClick={() => setOpenedModal(AvailabilitySettingsModalType.disconnectGoogleCalendar)}>Disconnect Google Calendar</ClosableMenuItem>}
            {Boolean(!icsSubscription) && <ClosableMenuItem onClick={() => setOpenedModal(AvailabilitySettingsModalType.connectGoogleCalendar)}>Connect Google Calendar</ClosableMenuItem>}
          </>
        )}
        {USER_PUBLIC_ICS_SUBSCRIPTION && (
          <ClosableMenuItem onClick={async () => setSubscribeToPublicICSMModalOpen(true)}>
            Subscribe to calendar
          </ClosableMenuItem>
        )}
      </ClosableMenu>
      <EditTimezoneModal open={openedModal === AvailabilitySettingsModalType.timezone} onClose={() => setOpenedModal(null)} />
      <EditIdealCaseloadModal open={openedModal === AvailabilitySettingsModalType.idealCaseload} onClose={() => setOpenedModal(null)} />
      {!isMobile && (
        <>
          <UnsubscribeGoogleCalendarModal open={openedModal === AvailabilitySettingsModalType.disconnectGoogleCalendar} onClose={handleOnClose} />
          <SubscribeGoogleCalendarModal open={openedModal === AvailabilitySettingsModalType.connectGoogleCalendar} onClose={handleOnClose} />
        </>
      )}
      <PublicICSSubscriptionModal open={subscribeToPublicICSModalOpen} onClose={() => setSubscribeToPublicICSMModalOpen(false)} />
    </>
  );
};
